// Utilidades compartidas del módulo Control de Ganado (esquema ganaderia).
// Todo el acceso a datos va por RPC (gan_*) — no hay CRUD REST directo a tablas.
// La escritura exige nivel de acceso >=5 (lo valida el server); los errores se
// muestran con toast (getErrorMessage), sin gating extra en el cliente.
import { useState } from 'react';
import { useMutation, useQuery, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, fieldCls } from '@/features/inventario/shared';

// Reutilizamos los helpers de UI del módulo de inventario (mismo estándar visual).
export { Campo, Modal, SortTh, Switch, fieldCls, money, num, useSort } from '@/features/inventario/shared';

const GAN = { headers: { 'Content-Profile': 'ganaderia', 'Accept-Profile': 'ganaderia' } };

// Llamada a una RPC del esquema ganaderia.
export const ganRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, GAN)).data;

// ---------------------------------------------------------------- Tipos ----
export interface Predio {
  id: string; nombre: string; codigo_sifae: string | null; ruc_ganadero: string | null;
  direccion: string | null; lat: number | null; lng: number | null; activo: boolean;
}

export interface Potrero {
  id: string; predio_id: string; predio_nombre: string; nombre: string;
  area_ha: number | null; activo: boolean;
}

export type Proposito = 'leche' | 'carne' | 'cria' | 'mixto';
export interface Lote {
  id: string; predio_id: string; predio_nombre: string; nombre: string;
  proposito: Proposito; activo: boolean;
}
export const PROPOSITOS: { v: Proposito; label: string }[] = [
  { v: 'leche', label: 'Leche' },
  { v: 'carne', label: 'Carne' },
  { v: 'cria', label: 'Cría' },
  { v: 'mixto', label: 'Mixto' },
];
export const propositoLabel = (p: string | null) => PROPOSITOS.find((x) => x.v === p)?.label ?? (p ?? '—');

export type SexoAnimal = 'M' | 'H';
export type EstadoAnimal = 'activo' | 'vendido' | 'muerto' | 'faenado' | 'descartado';
export type Procedencia = 'nacido' | 'comprado';

export interface Animal {
  id: string; arete: string; nombre: string | null; especie: string | null; raza: string | null;
  sexo: SexoAnimal; fecha_nacimiento: string | null; categoria: string | null; estado: EstadoAnimal;
  lote_id: string | null; lote_nombre: string | null; potrero_id: string | null; potrero_nombre: string | null;
  predio_id: string | null; predio_nombre: string | null; procedencia: Procedencia | null;
  edad_meses: number | null; madre_arete: string | null; padre_arete: string | null;
  observaciones?: string | null; costo_inicial?: number | null;
  madre_id?: string | null; padre_id?: string | null;
}

export interface Pesaje { fecha: string; peso_kg: number; tipo: string }
export interface MovimientoRow {
  fecha: string; tipo: string; potrero_origen: string | null; potrero_destino: string | null;
  peso_kg: number | null; precio: number | null; motivo: string | null;
}
export interface AnimalFicha {
  animal: Animal;
  pesajes: Pesaje[];
  movimientos: MovimientoRow[];
  gdp: number | null;
}

export interface DashboardData {
  total_activos: number;
  por_categoria: { categoria: string; n: number }[];
  por_estado: { estado: string; n: number }[];
  por_lote: { lote: string; n: number }[];
}

export const ESTADOS_ANIMAL: { v: EstadoAnimal; label: string; cls: string }[] = [
  { v: 'activo', label: 'Activo', cls: 'bg-estable-soft text-estable' },
  { v: 'vendido', label: 'Vendido', cls: 'bg-secondary text-secondary-foreground' },
  { v: 'muerto', label: 'Muerto', cls: 'bg-urgente-soft text-urgente' },
  { v: 'faenado', label: 'Faenado', cls: 'bg-muted text-muted-foreground' },
  { v: 'descartado', label: 'Descartado', cls: 'bg-pendiente-soft text-pendiente' },
];
export const estadoInfo = (e: string | null | undefined): { v: string; label: string; cls: string } =>
  ESTADOS_ANIMAL.find((x) => x.v === e) ?? { v: e ?? '—', label: e ?? '—', cls: 'bg-muted text-muted-foreground' };

export const SEXOS: { v: SexoAnimal; label: string }[] = [{ v: 'M', label: 'Macho' }, { v: 'H', label: 'Hembra' }];
export const sexoLabel = (s: string | null) => (s === 'M' ? 'Macho' : s === 'H' ? 'Hembra' : '—');

export const PROCEDENCIAS: { v: Procedencia; label: string }[] = [
  { v: 'nacido', label: 'Nacido en el predio' },
  { v: 'comprado', label: 'Comprado' },
];

export const TIPOS_MOVIMIENTO: { v: string; label: string }[] = [
  { v: 'nacimiento', label: 'Nacimiento' },
  { v: 'compra', label: 'Compra' },
  { v: 'venta', label: 'Venta' },
  { v: 'muerte', label: 'Muerte' },
  { v: 'faena', label: 'Faena' },
  { v: 'descarte', label: 'Descarte' },
  { v: 'traslado', label: 'Traslado' },
];
export const tipoMovLabel = (t: string) => TIPOS_MOVIMIENTO.find((x) => x.v === t)?.label ?? t;

export const TIPOS_PESAJE: { v: string; label: string }[] = [
  { v: 'nacimiento', label: 'Nacimiento' },
  { v: 'destete', label: 'Destete' },
  { v: 'periodico', label: 'Periódico' },
];

// meses -> "2 a 3 m" / "8 m"
export const edadTexto = (meses: number | null | undefined): string => {
  if (meses == null) return '—';
  const m = Math.round(Number(meses));
  if (m < 12) return `${m} m`;
  const anios = Math.floor(m / 12);
  const rem = m % 12;
  return rem > 0 ? `${anios} a ${rem} m` : `${anios} a`;
};

// hoy en local (evita corrimiento UTC de <input type=date>)
export const hoyISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// -------------------------------------------------------- Partners (financiero) --
const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
export interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
export const partnersList = async (): Promise<PartnerOpt[]> =>
  (await apiClient.post<PartnerOpt[]>('/rpc/sri_partners', {}, FIN)).data || [];

// Campos de un movimiento según su tipo (para mostrar solo lo relevante en el form).
export function camposMovimiento(tipo: string) {
  return {
    potrero: ['traslado', 'compra', 'nacimiento'].includes(tipo),
    partner: ['compra', 'venta'].includes(tipo),
    precio: ['compra', 'venta', 'faena', 'descarte'].includes(tipo),
    motivo: ['muerte', 'faena', 'descarte', 'traslado'].includes(tipo),
  };
}

// Invalida todas las queries del módulo (hato, dashboard, catálogos) tras una
// operación que cambia estado/ubicación de un animal.
export const invalidateGanaderia = (qc: QueryClient): void => {
  void qc.invalidateQueries({
    predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('gan-'),
  });
};

// -------------------------------------------------- Registrar movimiento ----
// Reutilizable desde la Ficha del animal (Hato) y desde la pantalla Movimientos.
export function RegistrarMovimientoModal({ animal, onClose, onSaved }:
  { animal: Animal; onClose: () => void; onSaved: () => void }) {
  const [tipo, setTipo] = useState<string>('traslado');
  const [fecha, setFecha] = useState(hoyISO());
  const [potreroDestinoId, setPotreroDestinoId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [pesoKg, setPesoKg] = useState('');
  const [precio, setPrecio] = useState('');
  const [motivo, setMotivo] = useState('');
  const campos = camposMovimiento(tipo);

  const { data: potreros = [] } = useQuery<Potrero[]>({ queryKey: ['gan-potreros'], queryFn: () => ganRpc<Potrero[]>('gan_potrero_listar') });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: partnersList });
  const partnersFiltrados = partners.filter((p) => (p.tipo_partner || []).includes(tipo === 'compra' ? 'proveedor' : 'cliente'));

  const registrar = useMutation({
    mutationFn: () => ganRpc('gan_movimiento_registrar', {
      _data: {
        animal_id: animal.id, tipo, fecha,
        potrero_origen_id: animal.potrero_id || null,
        potrero_destino_id: campos.potrero ? (potreroDestinoId || null) : null,
        partner_id: campos.partner ? (partnerId || null) : null,
        peso_kg: pesoKg === '' ? null : Number(pesoKg),
        precio: campos.precio && precio !== '' ? Number(precio) : null,
        motivo: motivo || null,
      },
    }),
    onSuccess: () => { toast.success('Movimiento registrado'); onSaved(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Registrar movimiento · ${animal.arete}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Tipo *">
            <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS_MOVIMIENTO.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Campo>
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
        </div>
        {campos.potrero && (
          <Campo label="Potrero destino">
            <select className={fieldCls} value={potreroDestinoId} onChange={(e) => setPotreroDestinoId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {potreros.map((p) => <option key={p.id} value={p.id}>{p.predio_nombre} · {p.nombre}</option>)}
            </select>
          </Campo>
        )}
        {campos.partner && (
          <Campo label={tipo === 'compra' ? 'Proveedor' : 'Cliente'}>
            <select className={fieldCls} value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {partnersFiltrados.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
            </select>
          </Campo>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Peso (kg)"><input type="number" min="0" step="0.1" className={fieldCls} value={pesoKg} onChange={(e) => setPesoKg(e.target.value)} /></Campo>
          {campos.precio && (
            <Campo label="Precio ($)"><input type="number" min="0" step="0.01" className={fieldCls} value={precio} onChange={(e) => setPrecio(e.target.value)} /></Campo>
          )}
        </div>
        {campos.motivo && (
          <Campo label="Motivo"><textarea className={`${fieldCls} h-16`} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={registrar.isPending} onClick={() => registrar.mutate()}>Registrar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ===========================================================================
// Fase 2A: Sanidad y Reproducción (script 667)
// ===========================================================================

// ------------------------------------------------------------- Sanidad -----
export type TipoPlanItem = 'vacuna' | 'desparasitacion' | 'vitamina' | 'prueba' | 'otro';
export const TIPOS_PLAN_ITEM: { v: TipoPlanItem; label: string }[] = [
  { v: 'vacuna', label: 'Vacuna' },
  { v: 'desparasitacion', label: 'Desparasitación' },
  { v: 'vitamina', label: 'Vitamina' },
  { v: 'prueba', label: 'Prueba diagnóstica' },
  { v: 'otro', label: 'Otro' },
];
export const tipoPlanItemLabel = (t: string) => TIPOS_PLAN_ITEM.find((x) => x.v === t)?.label ?? t;

export type BaseCalculo = 'edad' | 'intervalo';

export const VIAS_APLICACION = ['Oral', 'Intramuscular', 'Subcutánea', 'Intravenosa', 'Tópica', 'Nasal', 'Otra'];

export interface PlanItem {
  id?: string; tipo: TipoPlanItem; nombre: string; producto: string | null; base_calculo: BaseCalculo;
  edad_dias: number | null; intervalo_dias: number | null; obligatoria: boolean;
  retiro_leche_dias: number | null; retiro_carne_dias: number | null; orden: number;
}
export interface PlanSanitario {
  id: string; nombre: string; especie: string | null; activo: boolean;
  created_at: string; updated_at: string; items: PlanItem[];
}

export interface SanidadEvento {
  id: string; animal_id: string; arete: string; nombre: string | null; fecha: string;
  tipo: string; nombre_evento: string; producto: string | null; dosis: string | null; via: string | null;
  lote_producto: string | null; responsable: string | null; plan_item_id: string | null;
  diagnostico: string | null; retiro_leche_hasta: string | null; retiro_carne_hasta: string | null;
  costo: number | null; proximo_refuerzo: string | null; observaciones: string | null;
}

export interface SanidadPendiente {
  animal_id: string; arete: string; nombre_animal: string | null; lote_nombre: string | null;
  item: string; tipo: string; fecha_venc: string; dias: number; estado: 'vencida' | 'proxima'; obligatoria: boolean;
}

export interface SanidadRetiro {
  animal_id: string; arete: string; nombre: string | null;
  retiro_leche_hasta: string | null; retiro_carne_hasta: string | null;
}

export const esAftosa = (item: string | null | undefined): boolean => !!item && item.toLowerCase().includes('aftosa');

// ---------------------------------------------------------- Reproducción ---
export type TipoRepro = 'celo' | 'servicio' | 'dx_prenez' | 'parto' | 'aborto' | 'secado' | 'destete';
export const TIPOS_REPRO: { v: TipoRepro; label: string }[] = [
  { v: 'celo', label: 'Celo' },
  { v: 'servicio', label: 'Servicio' },
  { v: 'dx_prenez', label: 'Diagnóstico de preñez' },
  { v: 'parto', label: 'Parto' },
  { v: 'aborto', label: 'Aborto' },
  { v: 'secado', label: 'Secado' },
  { v: 'destete', label: 'Destete' },
];
export const tipoReproLabel = (t: string) => TIPOS_REPRO.find((x) => x.v === t)?.label ?? t;

export type MetodoRepro = 'monta' | 'ia' | 'iatf';
export const METODOS_REPRO: { v: MetodoRepro; label: string }[] = [
  { v: 'monta', label: 'Monta natural' },
  { v: 'ia', label: 'Inseminación artificial' },
  { v: 'iatf', label: 'IATF' },
];
export const metodoReproLabel = (m: string | null) => METODOS_REPRO.find((x) => x.v === m)?.label ?? (m ?? '—');

export const RESULTADOS_DX = [{ v: 'prenada', label: 'Preñada' }, { v: 'vacia', label: 'Vacía' }];
export const RESULTADOS_PARTO = [
  { v: 'normal', label: 'Normal' }, { v: 'distocico', label: 'Distócico' }, { v: 'cesarea', label: 'Cesárea' },
];
// Campos condicionales del formulario de evento según el tipo elegido.
export function camposRepro(tipo: string) {
  return {
    metodo: tipo === 'servicio',
    toro: tipo === 'servicio',
    resultadoDx: tipo === 'dx_prenez',
    resultadoParto: tipo === 'parto',
    cria: tipo === 'parto',
    diasGestacion: ['dx_prenez', 'parto', 'aborto'].includes(tipo),
  };
}

export interface ReproEvento {
  id: string; hembra_id: string; hembra_arete: string; hembra_nombre: string | null; fecha: string;
  tipo: TipoRepro; metodo: MetodoRepro | null; toro_id: string | null; toro_arete: string | null;
  semental: string | null; resultado: string | null; cria_id: string | null; cria_arete: string | null;
  sexo_cria: SexoAnimal | null; dias_gestacion: number | null; costo: number | null; observaciones: string | null;
}

export interface ReproCalendarioItem {
  hembra_id: string; arete: string; nombre: string | null; evento_sugerido: string;
  fecha_sugerida: string; dias: number; estado: 'vencido' | 'proximo';
}

export interface ReproKpis {
  total_servicios: number; total_dx: number; prenadas: number; tasa_prenez: number | null;
  servicios_por_prenez: number | null; total_partos: number; total_abortos: number; total_nacimientos: number;
  iep_dias: number | null; dias_abiertos: number | null; desde: string | null; hasta: string | null;
}

// --------------------------------------------------- Registrar pesaje ------
// Reutilizable desde la Ficha del animal (Hato).
export function RegistrarPesajeModal({ animal, onClose, onSaved }:
  { animal: Animal; onClose: () => void; onSaved: () => void }) {
  const [fecha, setFecha] = useState(hoyISO());
  const [pesoKg, setPesoKg] = useState('');
  const [tipo, setTipo] = useState<string>('periodico');

  const registrar = useMutation({
    mutationFn: () => ganRpc('gan_pesaje_registrar', {
      _data: { animal_id: animal.id, fecha, peso_kg: pesoKg === '' ? null : Number(pesoKg), tipo },
    }),
    onSuccess: () => { toast.success('Pesaje registrado'); onSaved(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Registrar pesaje · ${animal.arete}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Campo label="Peso (kg) *"><input type="number" min="0" step="0.1" className={fieldCls} value={pesoKg} onChange={(e) => setPesoKg(e.target.value)} /></Campo>
        </div>
        <Campo label="Tipo">
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {TIPOS_PESAJE.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={registrar.isPending} disabled={pesoKg === ''} onClick={() => registrar.mutate()}>Registrar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ===========================================================================
// Fase 2B: Control lechero (script 668)
// ===========================================================================

export type Turno = 'am' | 'pm' | 'total';
export const TURNOS: { v: Turno; label: string }[] = [
  { v: 'am', label: 'AM' },
  { v: 'pm', label: 'PM' },
  { v: 'total', label: 'Total (tanque)' },
];
export const turnoLabel = (t: string) => TURNOS.find((x) => x.v === t)?.label ?? t;

export interface LecheRegistro {
  id: string; animal_id: string | null; arete: string | null; nombre: string | null;
  lote_id: string | null; lote_nombre: string | null; fecha: string; turno: Turno;
  litros: number; grasa_pct: number | null; proteina_pct: number | null; observaciones: string | null;
}

export interface LecheResumen {
  litros_total: number; dias: number; promedio_diario: number | null;
  grasa_prom: number | null; proteina_prom: number | null;
  por_lote: { lote: string; litros: number }[];
  top_animales: { arete: string; nombre: string | null; litros: number }[];
  serie: { fecha: string; litros: number }[];
  desde: string; hasta: string;
}

// ===========================================================================
// Fase 2C: Costos y ventas (script 669)
// ===========================================================================

export type CategoriaCosto = 'alimentacion' | 'sanidad' | 'mano_obra' | 'servicios' | 'depreciacion' | 'otro';
export const CATEGORIAS_COSTO: { v: CategoriaCosto; label: string }[] = [
  { v: 'alimentacion', label: 'Alimentación' },
  { v: 'sanidad', label: 'Sanidad' },
  { v: 'mano_obra', label: 'Mano de obra' },
  { v: 'servicios', label: 'Servicios' },
  { v: 'depreciacion', label: 'Depreciación' },
  { v: 'otro', label: 'Otro' },
];
export const categoriaCostoLabel = (c: string) => CATEGORIAS_COSTO.find((x) => x.v === c)?.label ?? c;

export type OrigenCosto = 'manual' | 'insumo' | 'sanidad' | 'venta';
export const origenCostoLabel = (o: string) =>
  ({ manual: 'Manual', insumo: 'Insumo (inventario)', sanidad: 'Sanidad', venta: 'Venta' }[o] ?? o);

export interface Costo {
  id: string; fecha: string; categoria: CategoriaCosto; concepto: string;
  lote_id: string | null; lote_nombre: string | null; animal_arete: string | null; predio_nombre: string | null;
  cantidad: number | null; valor: number; origen: OrigenCosto; asiento_id: string | null;
  referencia_inventario: string | null; observaciones: string | null;
  producto_id?: string | null; location_id?: string | null;
  centro_costo_id?: string | null; proyecto_id?: string | null;
}

export interface RentabilidadLote {
  lote_id: string | null; lote: string; costo_total: number; ingreso_total: number; margen: number;
  costo_por_categoria: Record<string, number>;
}
export interface Rentabilidad {
  por_lote: RentabilidadLote[];
  totales: { costo_total: number; ingreso_total: number; margen: number };
  desde: string; hasta: string;
}

export interface VentaResultado {
  ok: boolean; vendidos: number; ticket_id?: string; importe_total?: number; message: string;
}

// -------------------------------------------------- Proyectos (contabilidad) --
const CONT = { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } };
export interface ProyectoOpt { id: string; codigo: string; nombre: string; estado?: string }
export const proyectosContablesList = async (): Promise<ProyectoOpt[]> =>
  (await apiClient.post<ProyectoOpt[]>('/rpc/cont_proyectos', {}, CONT)).data || [];
